import { DesignViewer, MenuDesign } from "@streamllc/figma-design-viewer"
import { useEffect, useMemo } from "react"

import { Design } from "@/entities/design"
import { loadGoogleFont, loadPrivateFonts } from "@/entities/fonts/fonts.helpers"

import { EventBasicsData } from "./Step1EventBasics"

interface Step3ReviewConfirmProps {
  data: EventBasicsData
  selectedDesign: Design | null
  companyId: string
}

const DAYS_MAP: Record<string, string> = {
  Sunday: "Sun",
  Monday: "Mon",
  Tuesday: "Tue",
  Wednesday: "Wed",
  Thursday: "Thu",
  Friday: "Fri",
  Saturday: "Sat"
}

function Step3ReviewConfirm(props: Step3ReviewConfirmProps) {
  const { data, selectedDesign, companyId } = props

  // Parse design JSON and create MenuDesign instance for DesignViewer
  const designData = useMemo(() => {
    if (!selectedDesign) return null
    try {
      const node = JSON.parse(selectedDesign.json)
      return new MenuDesign(node)
    } catch (error) {
      console.error("Failed to parse design JSON:", error)
      return null
    }
  }, [selectedDesign])

  // Load fonts for the design
  useEffect(() => {
    if (designData) {
      designData.fonts.forEach(font => loadGoogleFont(font.family))
      void loadPrivateFonts(companyId)
    }
  }, [designData, companyId])

  // Resolver for image hashes - same pattern as MenuDesignPreview
  function getMediaUrl(hash: string): string {
    const url = new URL(`designImage-${companyId}-${hash}`, import.meta.env.VITE_STORAGE_URL)
    return url.toString()
  }

  function formatDateTime(date: string, time: string): string {
    if (!date || !time) return ""
    const dateObj = new Date(date)
    const month = dateObj.toLocaleDateString("en-US", { month: "short" })
    const day = dateObj.getDate()
    const year = dateObj.getFullYear().toString().slice(-2)

    // Convert 24h time to 12h format
    const [hours, minutes] = time.split(":")
    const hour = parseInt(hours)
    const ampm = hour >= 12 ? "PM" : "AM"
    const hour12 = hour % 12 || 12

    return `${month} ${day}, ${year}  ${hour12}:${minutes} ${ampm}`
  }

  function formatWeekDays(): string {
    return data.selectedDays.map(day => DAYS_MAP[day] || day).join(", ")
  }

  function getTimingLabel(): string {
    if (data.recurrenceMode === "daily") {
      return "Daily Schedule"
    }
    if (data.recurrenceMode === "specific_days") {
      return "Weekly Schedule"
    }
    return "One-Time Event"
  }

  function getActivationMode(): string {
    if (data.recurrenceMode === "daily") {
      return "Repeats Daily"
    }
    if (data.recurrenceMode === "specific_days") {
      return "Repeats on Selected Days"
    }
    return "Specific Date Range"
  }

  function formatTime(time: string): string {
    if (!time) return ""
    const [hours, minutes] = time.split(":")
    const hour = parseInt(hours)
    const ampm = hour >= 12 ? "PM" : "AM"
    const hour12 = hour % 12 || 12
    return `${hour12}:${minutes} ${ampm}`
  }

  function getActivePeriod(): string {
    if (data.recurrenceMode === "daily") {
      // Daily mode: just show the time range
      return `${formatTime(data.startTime)} - ${formatTime(data.endTime)}`
    }

    if (data.recurrenceMode === "specific_days") {
      // Specific days mode: show time range
      return `${formatTime(data.startTime)} - ${formatTime(data.endTime)}`
    }

    // Specific dates mode: show full date and time range
    const startStr = formatDateTime(data.startDate, data.startTime)
    const endStr = formatDateTime(data.endDate, data.endTime)
    return `${startStr} - ${endStr}`
  }

  return (
    <div className="step3-review-confirm">
      <div className="step3-review-confirm__section">
        {selectedDesign && designData ? (
          <>
            <label className="step3-review-confirm__label">Design Selected:</label>
            <div className="step3-review-confirm__design">
              <div className="step3-review-confirm__design-preview">
                <DesignViewer
                  design={designData}
                  resolvers={{
                    imageHash: getMediaUrl,
                    transformText: content => content,
                    transformWidget() { }
                  }}
                  options={{ fit: "contain" }}
                />
              </div>
              <div className="step3-review-confirm__design-name">{selectedDesign.name}</div>
            </div>
          </>
        ) : (
          <>
            <label className="step3-review-confirm__label">Design Selected:</label>
            <div className="step3-review-confirm__no-design">No design selected</div>
          </>
        )}
      </div>

      <div className="step3-review-confirm__info">
        <label className="step3-review-confirm__label">Internal Information:</label>
        <div className="step3-review-confirm__info-card">
          <div className="step3-review-confirm__info-row">
            <span className="step3-review-confirm__info-label">Scheduler Name:</span>
            <span className="step3-review-confirm__info-value">{data.name}</span>
          </div>
          <div className="step3-review-confirm__info-row">
            <span className="step3-review-confirm__info-label">Timing:</span>
            <span className="step3-review-confirm__info-value">{getTimingLabel()}</span>
          </div>
          <div className="step3-review-confirm__info-row">
            <span className="step3-review-confirm__info-label">Activation Mode:</span>
            <span className="step3-review-confirm__info-value">{getActivationMode()}</span>
          </div>
          <div className="step3-review-confirm__info-row">
            <span className="step3-review-confirm__info-label">Active Period:</span>
            <span className="step3-review-confirm__info-value">{getActivePeriod()}</span>
          </div>
          {data.recurrenceMode === "specific_days" && data.selectedDays.length > 0 && (
            <div className="step3-review-confirm__info-row">
              <span className="step3-review-confirm__info-label">Week Frequency:</span>
              <div className="step3-review-confirm__days">
                {data.selectedDays.map(day => (
                  <span key={day} className="step3-review-confirm__day-badge">
                    {DAYS_MAP[day]}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="step3-review-confirm__notes">
        <label className="step3-review-confirm__label">Notes:</label>
        <div className="step3-review-confirm__notes-content">
          {data.description}
        </div>
      </div>
    </div>
  )
}

export default Step3ReviewConfirm
